import {
  Download,
  FilePlus2,
  FolderOpen,
  GraduationCap,
  Menu,
  Keyboard,
  Play,
  Redo2,
  Save,
  Square,
  Undo2,
} from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { engine } from '../audio/engine';
import { loopLength } from '../audio/sequencer';
import { downloadBlob, safeName } from '../audio/wav';
import { TapTempo } from '../lib/audition';
import { useBeat } from '../lib/hooks';
import { createProject } from '../store/defaults';
import { deserializeProject, serializeProject } from '../store/persist';
import { clearSession } from '../store/session';
import { useStore, type View } from '../store/useStore';
import { Logo } from './Logo';
import { Popover } from './ui/Popover';
import { Spectrum } from './ui/Spectrum';
import { NumberField, Segmented } from './ui/Controls';

function MenuItem({
  icon,
  label,
  hint,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  hint?: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-volt-500/10"
    >
      <span className="text-ink-300">{icon}</span>
      <span className="flex-1 text-[11px]">{label}</span>
      {hint && <span className="text-[9px] text-ink-500">{hint}</span>}
    </button>
  );
}

/** Abbreviated below xl — "Sequence" alone is 30px this bar can't always spare. */
function ViewLabel({ full, short }: { full: string; short: string }) {
  return (
    <>
      <span className="xl:hidden">{short}</span>
      <span className="hidden xl:inline">{full}</span>
    </>
  );
}

const VIEWS: { value: View; label: ReactNode }[] = [
  { value: 'chop', label: <ViewLabel full="Chop" short="Chop" /> },
  { value: 'sequence', label: <ViewLabel full="Sequence" short="Seq" /> },
  { value: 'song', label: <ViewLabel full="Song" short="Song" /> },
  { value: 'mix', label: <ViewLabel full="Mix" short="Mix" /> },
];

export function TopBar({
  onExport,
  onShortcuts,
  onTour,
}: {
  onExport: () => void;
  onShortcuts: () => void;
  onTour: () => void;
}) {
  const project = useStore((s) => s.project);
  const view = useStore((s) => s.view);
  const playMode = useStore((s) => s.playMode);
  const playing = useStore((s) => s.playing);
  const store = useStore();
  const beat = useBeat();
  const tap = useRef(new TapTempo());
  const fileRef = useRef<HTMLInputElement>(null);
  const [tapHint, setTapHint] = useState<number | null>(null);

  const bpb = project.beatsPerBar;
  const loop = loopLength(project, playMode);
  const bar = Math.floor(beat / bpb) + 1;
  const beatInBar = Math.floor(beat % bpb) + 1;
  const tick = Math.floor(((beat % 1) * 96) / 1);

  const save = async () => {
    const blob = await serializeProject(project);
    downloadBlob(blob, `${safeName(project.name)}.chop`);
    store.notify('Project saved');
  };

  return (
    <header className="flex items-center gap-2 border-b border-ink-700 bg-ink-900 px-2.5 py-2 xl:gap-3 xl:px-3">
      <div className="flex items-center gap-2">
        <Logo size={22} />
        <input
          className="display w-20 bg-transparent text-base outline-none xl:w-28"
          value={project.name}
          onChange={(e) => store.set((p) => void (p.name = e.target.value), false)}
        />
      </div>

      <div className="flex items-center gap-1" data-tour="transport">
        <button
          className={`btn ${playing ? 'btn-active breathe' : 'btn-primary'}`}
          onClick={() => {
            if (playing) {
              engine.stop();
              store.setUi({ playing: false });
            } else {
              void engine.start(project, playMode, 0);
              store.setUi({ playing: true });
            }
          }}
          title="Play / stop (space)"
        >
          {playing ? <Square size={13} /> : <Play size={13} />}
          <span className="hidden lg:inline">{playing ? 'Stop' : 'Play'}</span>
        </button>

        <Segmented
          value={playMode}
          onChange={(m) => {
            store.setUi({ playMode: m });
            if (playing) void engine.start(project, m, 0);
          }}
          options={[
            { value: 'pattern', label: 'Pattern' },
            { value: 'song', label: 'Song' },
          ]}
        />
      </div>

      <div className="mono flex items-baseline gap-1 rounded-lg border border-ink-700 bg-ink-850 px-2.5 py-1 text-sm tabular-nums">
        <span className="text-volt-400">{String(bar).padStart(2, '0')}</span>
        <span className="text-ink-600">.</span>
        <span>{beatInBar}</span>
        <span className="text-ink-600">.</span>
        <span className="text-ink-400 text-xs">{String(tick).padStart(2, '0')}</span>
        <span className="ml-1.5 hidden text-[9px] text-ink-500 lg:inline">{loop.toFixed(0)}b loop</span>
      </div>

      <div className="flex items-end gap-1">
        <label className="flex flex-col gap-0.5">
          <span className="label leading-none">Project BPM</span>
          <NumberField
            value={project.bpm}
            min={20}
            max={400}
            step={0.5}
            width="w-20"
            onChange={(v) => store.set((p) => void (p.bpm = Math.max(20, Math.min(400, v))))}
          />
        </label>
        <button
          className="btn"
          title="Tap four or more times to set the tempo"
          onClick={() => {
            const bpm = tap.current.tap();
            setTapHint(bpm);
            if (bpm) store.set((p) => void (p.bpm = bpm), false);
          }}
        >
          Tap{tapHint ? ` ${tapHint.toFixed(0)}` : ''}
        </button>
        <label className="hidden flex-col gap-0.5 2xl:flex">
          <span className="label leading-none">Swing</span>
          <input
            type="range"
            className="w-20"
            min={0}
            max={0.75}
            step={0.01}
            value={project.swing}
            onChange={(e) => store.set((p) => void (p.swing = parseFloat(e.target.value)), false)}
          />
        </label>
      </div>

      <Segmented value={view} onChange={(v) => store.setUi({ view: v })} options={VIEWS} />

      <div className="ml-auto flex items-center gap-1">
        <Spectrum analyser={engine.masterAnalyser()} bars={20}
          className="hidden h-7 w-24 rounded border border-ink-700 xl:block" />
        <button className="btn btn-icon" title="Undo (⌘Z)" onClick={() => store.undo()}>
          <Undo2 size={13} />
        </button>
        <button className="btn btn-icon" title="Redo (⇧⌘Z)" onClick={() => store.redo()}>
          <Redo2 size={13} />
        </button>

        {/* Five icon buttons of file and help actions ate 175px of a bar that
            didn't have it. They're also more findable with labels on them. */}
        <Popover compact label="Project and help" icon={<Menu size={13} />} align="right" width="w-56">
          <div className="space-y-0.5">
            <MenuItem icon={<FilePlus2 size={13} />} label="New project" hint="Clears the saved session"
              onClick={() => {
                if (!confirm('Start a new project? The current one will be cleared.')) return;
                engine.stop();
                void clearSession();
                store.loadProject(createProject());
                store.setUi({ view: 'chop', selectedTrackId: null, selectedSourceId: null, selectedSteps: [] });
                store.notify('New project');
              }}
            />
            <MenuItem icon={<FolderOpen size={13} />} label="Open project…" hint=".chop file"
              onClick={() => fileRef.current?.click()} />
            <MenuItem icon={<Save size={13} />} label="Save project" hint="Audio included"
              onClick={() => void save()} />
            <div className="my-1 h-px bg-ink-700" />
            <MenuItem icon={<GraduationCap size={13} />} label="Take the tour" onClick={onTour} />
            <MenuItem icon={<Keyboard size={13} />} label="Keyboard shortcuts" onClick={onShortcuts} />
          </div>
        </Popover>

        <input
          ref={fileRef}
          type="file"
          accept=".chop,.wavchop,application/json"
          hidden
          onChange={async (e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) return;
            try {
              store.loadProject(await deserializeProject(file));
              store.notify('Project loaded');
            } catch (err) {
              store.notify(`Could not open: ${(err as Error).message}`);
            }
          }}
        />
        <button className="btn btn-primary" data-tour="export" onClick={onExport}>
          <Download size={13} />
          Export
        </button>
      </div>
    </header>
  );
}
